import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import {
    quoteItemsApi,
    useGetQuoteItemsQuery,
    useLazyGetQuoteItemsQuery,
    useUpdateQuoteItemMutation,
} from '@/app/services/quoteItems'
import { selectLocale } from '@/app/slices/appSlice'
import { selectCurrencyCode, selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { getCurrencyFormat } from '@/common/utils'

const Folding = ({ quoteItemId }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { quoteId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)

    const currencyCode = useSelector(selectCurrencyCode)
    const locale = useSelector(selectLocale)

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const [getQuoteItems] = useLazyGetQuoteItemsQuery()
    const [updateQuoteItem] = useUpdateQuoteItemMutation()

    const units = quoteItem?.isImperial ? 'in' : 'mm'

    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)
    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const handleUpdateQuoteItem = async (quoteItem) => {
        try {
            await updateQuoteItem({
                organisationId,
                quoteId,
                quoteItemId: quoteItem?.id,
                quoteItem,
            }).unwrap()
        } catch (error) {
            enqueueSnackbar(t('Failed to update the $t(quote) item'), {
                variant: 'error',
            })
        }
    }

    const debounceHandleUpdateQuoteItem = useDebouncedCallback(handleUpdateQuoteItem, 800)

    const handleSourceChange = async (event) => {
        const value = event?.target?.value
        if (!value || quoteItem?.foldingSource === value) return

        await handleUpdateQuoteItem({
            ...quoteItem,
            foldingSource: value,
        })

        if (value === 'Drawing') {
            await getQuoteItems({ organisationId, quoteId })
        }
    }

    const handleMaxLengthChange = (values) => {
        const { floatValue } = values
        debounceHandleUpdateQuoteItem.cancel()

        // Immediate cache update for responsive UI
        dispatch(
            // @ts-ignore
            quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                const item = draft.find((i) => i.id === quoteItemId)
                if (item) {
                    item.maxFoldLength = floatValue
                }
            })
        )

        debounceHandleUpdateQuoteItem({
            ...quoteItem,
            maxFoldLength: floatValue,
            numberOfFolds: quoteItem?.numberOfFolds,
        })
    }

    const handleNumberOfFoldsChange = (values) => {
        const { floatValue } = values
        debounceHandleUpdateQuoteItem.cancel()

        // Immediate cache update for responsive UI
        dispatch(
            // @ts-ignore
            quoteItemsApi.util.updateQueryData('getQuoteItems', { organisationId, quoteId }, (draft) => {
                const item = draft.find((i) => i.id === quoteItemId)
                if (item) {
                    item.numberOfFolds = floatValue
                }
            })
        )

        debounceHandleUpdateQuoteItem({
            ...quoteItem,
            numberOfFolds: floatValue,
            maxFoldLength: quoteItem?.maxFoldLength,
        })
    }

    return (
        <Box>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography>{t('Source')}</Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Select
                            data-testid="nqp-quote-item-source"
                            disabled={isPartFromLibrary}
                            id="select-itemSource"
                            labelId="select-itemSource-label"
                            size="small"
                            value={quoteItem?.foldingSource || ''}
                            fullWidth
                            onChange={handleSourceChange}
                        >
                            <MenuItem
                                key="drawing"
                                value="Drawing"
                            >
                                {t('Drawing')}
                            </MenuItem>
                            <MenuItem
                                key="manual"
                                value="Manual"
                            >
                                {t('Manual')}
                            </MenuItem>
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 2,
                    }}
                >
                    <Typography>{t('Max fold length')}</Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 2,
                        }}
                    >
                        <NumericFormat
                            allowNegative={false}
                            customInput={TextField}
                            decimalSeparator={currencyFormat.decimal}
                            disabled={quoteItem?.foldingSource === 'Drawing' || isPartFromLibrary}
                            inputProps={{
                                style: { textAlign: 'right' },
                                'data-testid': 'nqp-quote-item-max-length-quantity',
                            }}
                            placeholder={`0 ${units}`}
                            size="small"
                            suffix={` ${units}`}
                            thousandSeparator={currencyFormat.group}
                            value={quoteItem?.maxFoldLength ?? ''}
                            variant="outlined"
                            onValueChange={handleMaxLengthChange}
                        />
                    </Box>
                </TbxTooltip>
            </Box>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 2,
                    }}
                >
                    <Typography>{t('Number of folds')}</Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 2,
                        }}
                    >
                        <NumericFormat
                            allowNegative={false}
                            customInput={TextField}
                            decimalSeparator={currencyFormat.decimal}
                            disabled={quoteItem?.foldingSource === 'Drawing' || isPartFromLibrary}
                            inputProps={{
                                style: { textAlign: 'right' },
                                'data-testid': 'nqp-quote-item-number-of-folds-quantity',
                            }}
                            placeholder="0"
                            size="small"
                            thousandSeparator={currencyFormat.group}
                            value={quoteItem?.numberOfFolds ?? ''}
                            variant="outlined"
                            onValueChange={handleNumberOfFoldsChange}
                        />
                    </Box>
                </TbxTooltip>
            </Box>
        </Box>
    )
}

Folding.propTypes = {
    quoteItemId: PropTypes.string,
}

export default Folding
